"use client"

import {StateCreator} from "zustand/vanilla";
import {JobInfo, JobRun, ListJobsResponse} from "@devplaninc/codeup";
import {jobAPI, jobsFetchAPI, jobRunsAPI, taskRunsAPI} from "@/app/apiPaths";
import {FetchJobResponse, FetchJobRunsResponse, FetchJobsRequest} from "@/pb/api/devplan/services/web/company";
import {jr} from "@/hooks/queries/response-handler";

export interface JobsStoreState {
  jobs: Record<number, JobInfo[]>;
  // Job runs grouped by job id and ordered desc by creation time.
  jobRunsByJob: Record<string, string[]>;
  // Job runs grouped by task id (parentId) and ordered desc by creation time.
  taskJobRuns: Record<string, string[]>;
  jobRuns: Record<string, JobRun>;

  fetchJobs(companyId: number): Promise<void>;

  fetchJob(companyId: number, jobId: string): Promise<void>;

  fetchJobRuns(companyId: number, jobId: string): Promise<void>;

  fetchTaskJobRuns(companyId: number, taskId: string): Promise<void>;
}

export const createJobsSlice: StateCreator<
  JobsStoreState,
  [],
  [],
  JobsStoreState
> = (set) => ({
  jobs: {},
  jobRunsByJob: {},
  taskJobRuns: {},
  jobRuns:{},
  fetchJobs: async (companyId) => await fetch(jobsFetchAPI(companyId), {
    method: "POST",
    body: JSON.stringify(FetchJobsRequest.toJSON({companyId})),
  }).then(jr).then(js => {
    const {jobs, latestRuns} = ListJobsResponse.fromJSON(js);
    set((s) => {
      const updatedJobRuns = { ...s.jobRunsByJob };
      const jobRuns = {...s.jobRuns};

      // latestRuns is { [jobId: string]: JobRun }
      Object.entries(latestRuns).forEach(([jobId, run]) => {
        jobRuns[run.runId] = run;
        const existingRuns = updatedJobRuns[jobId] || [];

        // Find if this specific run already exists to replace it, otherwise append
        const runIndex = existingRuns.findIndex(r => r === run.runId);

        if (runIndex > -1) {
          // Replace existing run
          const newRuns = [...existingRuns];
          newRuns[runIndex] = run.runId;
          updatedJobRuns[jobId] = newRuns;
        } else {
          // Append new run (prepend if it's the latest)
          updatedJobRuns[jobId] = [run.runId, ...existingRuns];
        }
      });
      return {
        ...s,
        jobs: {...s.jobs, [companyId]: jobs},
        jobRunsByJob: updatedJobRuns,
        jobRuns,
      }
    });
  }),
  fetchJob: async (companyId, jobId) => await fetch(jobAPI(companyId, jobId)).then(jr).then(js => {
    const {jobJson} = FetchJobResponse.fromJSON(js);
    const job = jobJson ? JobInfo.fromJSON(JSON.parse(jobJson)) : undefined;
    if (!job) return;
    set((s) => {
      const companyJobs = s.jobs[companyId] || [];
      const existingIndex = companyJobs.findIndex(j => j.description?.id === job?.description?.id);

      const updatedJobs = existingIndex >= 0
        ? companyJobs.map((j, i) => i === existingIndex ? job : j)
        : [...companyJobs, job];

      return {
        ...s,
        jobs: {
          ...s.jobs,
          [companyId]: updatedJobs
        }
      };
    });
  }),
  fetchJobRuns: async (companyId, jobId) => await fetch(jobRunsAPI(companyId, jobId)).then(jr).then(js => {
    const {runsJson} = FetchJobRunsResponse.fromJSON(js);
    const runs = runsJson.map(r => JobRun.fromJSON(JSON.parse(r)));
    set((s) => ({
      ...s,
      jobRunsByJob: {
        ...s.jobRunsByJob,
        [jobId]: runs.map(r => r.runId),
      },
      jobRuns: {...s.jobRuns, ...runs.reduce((a, r) => ({...a, [r.runId]: r}), {})},
    }));
  }),
  fetchTaskJobRuns: async (companyId, taskId) => await fetch(taskRunsAPI(companyId, taskId)).then(jr).then(js => {
    const {runsJson} = FetchJobRunsResponse.fromJSON(js);
    const runs = runsJson.map(r => JobRun.fromJSON(JSON.parse(r)));
    // Sort by startedAt descending to ensure latest run is first
    runs.sort((a, b) => {
      const aTime = a.status?.startedAt?.getTime() ?? 0;
      const bTime = b.status?.startedAt?.getTime() ?? 0;
      return bTime - aTime;
    });
    set((s) => ({
      ...s,
      taskJobRuns: {
        ...s.taskJobRuns,
        [taskId]: runs.map(r => r.runId),
      },
      jobRuns: {...s.jobRuns, ...runs.reduce((a, r) => ({...a, [r.runId]: r}), {})}
    }));
  }),
});
